import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { APIConstant } from "services/api-constant";
import { Search, RefreshCw, Copy, Check, Mail, ChevronDown, Save } from "lucide-react";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;

const COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6"];
const initials = (f="",l="") => `${f[0]||""}${l[0]||""}`.toUpperCase()||"?";

// Suggest system role from designation level
const suggestRole = (designationLevel) => {
  const map = {
    Junior:"employee", Mid:"employee", Senior:"employee",
    Lead:"team_lead", Manager:"manager", Director:"senior_manager",
    "Vice President":"cto", Executive:"ceo",
  };
  return map[designationLevel] || "employee";
};

// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts. Works off
// whatever list is passed in (already filtered/searched) — caller
// controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 18px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const { T } = useTheme();

  // System roles — maps to portal access level
  const SYSTEM_ROLES = [
    { value:"employee",       label:"Employee",       color:T.green,  desc:"Employee portal only" },
    { value:"team_lead",      label:"Team Lead",      color:T.amber,  desc:"Employee + team view" },
    { value:"manager",        label:"Manager",        color:T.cyan,   desc:"Can approve leaves"   },
    { value:"senior_manager", label:"Senior Manager", color:T.purple, desc:"Oversees managers"    },
    { value:"cto",            label:"CTO",            color:T.red,    desc:"Technical leadership" },
    { value:"ceo",            label:"CEO",            color:T.red,    desc:"Top of org"           },
    { value:"company_admin",  label:"HR Admin",       color:T.cyan,   desc:"Full HR access"       },
  ];
  const roleColor = Object.fromEntries(SYSTEM_ROLES.map(r=>[r.value,r.color]));

  const token      = sessionStorage.getItem("platform_token") || "";
  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const companyId  = userData.company_id;
  const ax         = { headers:{ Authorization:`Bearer ${token}` } };

  const [employees,     setEmployees]     = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [designations,  setDesignations]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [deptFilter,    setDeptFilter]    = useState("");
  const [departments,   setDepartments]   = useState([]);
  const [rowEdits,      setRowEdits]      = useState({});  // empId → {role,designation_id,manager_id}
  const [saving,        setSaving]        = useState({});
  const [saved,         setSaved]         = useState({});
  const [copied,        setCopied]        = useState({});
  const [inviteLinks,   setInviteLinks]   = useState({});
  const [sending,       setSending]       = useState({});

  // ── Pagination ──────────────────────────────────────────────────
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  const load = async () => {
    try {
      setLoading(true);
      const [eRes, pRes, dRes, desRes] = await Promise.all([
        axios.get(`${APIConstant.employees}`, ax),
        axios.get(`${APIConstant.employees}`, ax).then(()=>
          axios.get(`${APIConstant.employees}`.replace("/employees/","/platform-users/") + `?company_id=${companyId}`, ax)
            .catch(()=>({ data:[] }))
        ),
        axios.get(`${APIConstant.departments}`, ax),
        axios.get(`${APIConstant.designations}`, ax),
      ]);
      setEmployees(eRes.data  || []);
      setPlatformUsers(pRes.data || []);
      setDepartments(dRes.data|| []);
      setDesignations(desRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Simpler: fetch employees and platform users separately
  const loadAll = async () => {
    try {
      setLoading(true);
      const [eRes, pRes, dRes, desRes] = await Promise.all([
        axios.get(`${API}/employees/`, ax),
        axios.get(`${API}/platform-users/?company_id=${companyId}`, ax).catch(()=>({data:[]})),
        axios.get(`${API}/departments/`, ax),
        axios.get(`${API}/designations/`, ax),
      ]);
      setEmployees(eRes.data   || []);
      setPlatformUsers(pRes.data|| []);
      setDepartments(dRes.data || []);
      setDesignations(desRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) loadAll(); }, [companyId]);

  const merged = useMemo(() => employees.map(emp => {
    const pu      = platformUsers.find(p => p.email === emp.email);
    const manager = employees.find(e => e.id === emp.manager_id);
    return { ...emp, platform_user:pu||null,
             manager_name: manager ? `${manager.first_name} ${manager.last_name}` : null };
  }), [employees, platformUsers]);

  const filtered = useMemo(() => merged.filter(e => {
    const s = search.toLowerCase();
    return (!search || `${e.first_name} ${e.last_name} ${e.email} ${e.employee_code||""}`.toLowerCase().includes(s))
      && (!deptFilter || String(e.department_id) === deptFilter);
  }), [merged, search, deptFilter]);

  // Reset to page 1 whenever the search term or department filter changes
  useEffect(() => { setPage(1); }, [search, deptFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const getEdit = (emp, field) => {
    if (rowEdits[emp.id]?.[field] !== undefined) return rowEdits[emp.id][field];
    if (field === "role")           return emp.platform_user?.role || suggestRole(emp.designation_level);
    if (field === "designation_id") return emp.designation_id || "";
    if (field === "manager_id")     return emp.manager_id || "";
    return "";
  };

  const setEdit = (empId, field, val) =>
    setRowEdits(p => ({ ...p, [empId]:{ ...p[empId], [field]:val } }));

  const hasPendingEdit = (empId) => !!rowEdits[empId] && Object.keys(rowEdits[empId]).length > 0;

  const saveRow = async (emp) => {
    const edits = rowEdits[emp.id] || {};
    if (!Object.keys(edits).length) return;
    setSaving(p=>({...p,[emp.id]:true}));
    try {
      const promises = [];

      // Update designation / manager on employee record
      if (edits.designation_id !== undefined || edits.manager_id !== undefined) {
        const payload = {};
        if (edits.designation_id !== undefined) payload.designation_id = edits.designation_id ? parseInt(edits.designation_id) : null;
        if (edits.manager_id     !== undefined) payload.manager_id     = edits.manager_id     ? parseInt(edits.manager_id)     : null;
        promises.push(axios.put(`${API}/employees/${emp.id}`, { ...emp, ...payload }, ax));
      }

      // Update role on platform user
      if (edits.role !== undefined && emp.platform_user) {
        promises.push(axios.put(`${API}/platform-users/${emp.platform_user.id}`, { role:edits.role }, ax));
      }

      await Promise.all(promises);
      setRowEdits(p => { const n={...p}; delete n[emp.id]; return n; });
      setSaved(p=>({...p,[emp.id]:true}));
      setTimeout(()=>setSaved(p=>({...p,[emp.id]:false})),2000);
      loadAll();
    } catch(e) { alert(e.response?.data?.detail || "Error saving"); }
    finally { setSaving(p=>({...p,[emp.id]:false})); }
  };

  const getInviteLink = async (emp) => {
    const pu  = emp.platform_user;
    setSending(p=>({...p,[emp.id]:true}));
    try {
      let token;
      if (pu) {
        const r = await axios.post(`${API}/platform-users/${pu.id}/resend-invite`, {}, ax);
        token = r.data.token;
      } else {
        const r = await axios.post(`${API}/platform-users/`, {
          email:emp.email, first_name:emp.first_name, last_name:emp.last_name,
          role: getEdit(emp,"role") || "employee",
          company_id:companyId, send_invite:true,
        }, ax);
        token = r.data.invite_token;
        loadAll();
      }
      if (token) {
        const url = `${window.location.origin}/setup-account?token=${token}&type=user`;
        setInviteLinks(p=>({...p,[emp.id]:url}));
      }
    } catch(e) { alert(e.response?.data?.detail || "Error"); }
    finally { setSending(p=>({...p,[emp.id]:false})); }
  };

  const copyLink = (empId, url) => {
    navigator.clipboard.writeText(url).catch(()=>{});
    setCopied(p=>({...p,[empId]:true}));
    setTimeout(()=>setCopied(p=>({...p,[empId]:false})),2500);
  };

  const accountStatus = (pu) => {
    if (!pu)               return { label:"No Account",   color:T.dim,   bg:T.toggle_bg };
    if (!pu.email_verified) return { label:"Invite Sent",  color:T.amber, bg:`${T.amber}1F` };
    if (!pu.is_active)     return { label:"Deactivated",  color:T.red,   bg:`${T.red}1F` };
    return                        { label:"Active",        color:T.green, bg:`${T.green}1F` };
  };

  const inpSt = { background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:7, color:T.text, fontSize:12, padding:"5px 8px", outline:"none", fontFamily:"Outfit,sans-serif", width:"100%" };
  const card  = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, fontFamily:"'Outfit',sans-serif", overflow:"hidden" };

  const activeCount = platformUsers.filter(p=>p.is_active&&p.email_verified).length;
  const pendingCount = platformUsers.filter(p=>!p.email_verified).length;
  const noAcctCount  = employees.filter(e=>!platformUsers.find(p=>p.email===e.email)).length;

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>EMPLOYEE MANAGEMENT</div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Users & Roles</h2>
        <p style={{ fontSize:13, color:T.muted, margin:0 }}>Edit role, designation and reporting for any employee. Changes auto-apply on Save.</p>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"Total Employees", val:employees.length, color:T.cyan },
          { label:"Active Accounts", val:activeCount,       color:T.green },
          { label:"Invite Pending",  val:pendingCount,      color:T.amber },
          { label:"No Account",      val:noAcctCount,       color:T.muted },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ ...card, padding:16, borderTop:`3px solid ${color}` }}>
            <div style={{ fontSize:24, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
            <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={card}>
        {/* Filters */}
        <div style={{ display:"flex", gap:10, padding:"13px 18px", borderBottom:`1px solid ${T.border}`, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ position:"relative", flex:1, minWidth:180 }}>
            <Search size={12} style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", color:T.dim }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, email, code…"
              style={{ ...inpSt, paddingLeft:27, fontSize:12.5 }}/>
          </div>
          <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)} style={{ ...inpSt, width:"auto", minWidth:150, fontSize:12.5, padding:"6px 10px" }}>
            <option value="" style={{background:T.opt_bg}}>All Departments</option>
            {departments.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>)}
          </select>
          <button onClick={loadAll} style={{ ...inpSt, width:"auto", cursor:"pointer", display:"flex", alignItems:"center", gap:5, color:T.muted, padding:"6px 10px" }}>
            <RefreshCw size={12}/>
          </button>
          <span style={{ fontSize:12, color:T.dim, marginLeft:"auto" }}>{filtered.length} employees</span>
        </div>

        {/* Role info */}
        <div style={{ padding:"8px 18px", borderBottom:`1px solid ${T.cyan}0F`, display:"flex", gap:14, flexWrap:"wrap" }}>
          {SYSTEM_ROLES.map(r => (
            <div key={r.value} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
              <div style={{ width:7, height:7, borderRadius:2, background:r.color }}/>
              <span style={{ color:r.color, fontWeight:600 }}>{r.label}</span>
              <span style={{ color:T.dim }}>— {r.desc}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:"center", color:T.muted, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:12 }}>
            <div style={{ width:22, height:22, border:`2px solid ${T.cyan}1A`, borderTopColor:T.cyan, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
            Loading…<style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"'Outfit',sans-serif" }}>
              <thead>
                <tr>{["Employee","System Role","Designation","Reports To","Account","Invite Link",""].map(h=>(
                  <th key={h} style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:T.dim, padding:"9px 14px", textAlign:"left", borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {paginated.length===0 ? (
                  <tr><td colSpan={7} style={{ textAlign:"center", padding:40, color:T.muted, fontSize:13 }}>No employees found</td></tr>
                ) : paginated.map((emp,i) => {
                  const pu      = emp.platform_user;
                  const status  = accountStatus(pu);
                  const link    = inviteLinks[emp.id];
                  const pending = hasPendingEdit(emp.id);
                  const curRole = getEdit(emp,"role");
                  const rColor  = roleColor[curRole] || T.muted;

                  return (
                    <tr key={emp.id}
                      style={{ background: pending ? `${T.amber}05` : "transparent", borderLeft: pending ? `2px solid ${T.amber}40` : "2px solid transparent" }}
                      onMouseEnter={e=>{ if(!pending) e.currentTarget.style.background=T.sidebar_hover; }}
                      onMouseLeave={e=>{ if(!pending) e.currentTarget.style.background="transparent"; }}>

                      {/* Employee */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                          <div style={{ width:32, height:32, borderRadius:8, background:emp.avatar_color||COLORS[i%COLORS.length], display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", flexShrink:0 }}>
                            {initials(emp.first_name,emp.last_name)}
                          </div>
                          <div>
                            <div style={{ fontSize:12.5, fontWeight:600 }}>{emp.first_name} {emp.last_name}</div>
                            <div style={{ fontSize:11, color:T.muted }}>{emp.employee_code}</div>
                          </div>
                        </div>
                      </td>

                      {/* System Role */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        <div style={{ position:"relative" }}>
                          <select value={curRole} onChange={e=>setEdit(emp.id,"role",e.target.value)}
                            style={{ ...inpSt, background:`${rColor}12`, border:`1px solid ${rColor}35`, color:rColor, fontWeight:600, paddingRight:22, appearance:"none", cursor:"pointer" }}>
                            {SYSTEM_ROLES.map(r=><option key={r.value} value={r.value} style={{ background:T.opt_bg, color:T.text }}>{r.label}</option>)}
                          </select>
                          <ChevronDown size={10} style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", color:rColor, pointerEvents:"none" }}/>
                        </div>
                        {!pu && <div style={{ fontSize:10, color:T.amber, marginTop:3 }}>No account yet</div>}
                      </td>

                      {/* Designation */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        <select value={getEdit(emp,"designation_id")} onChange={e=>setEdit(emp.id,"designation_id",e.target.value)}
                          style={{ ...inpSt, cursor:"pointer" }}>
                          <option value="" style={{background:T.opt_bg}}>— Unassigned —</option>
                          {designations.map(d=><option key={d.id} value={d.id} style={{ background:T.opt_bg }}>{d.title}{d.level?` (${d.level})`:""}</option>)}
                        </select>
                      </td>

                      {/* Reports To */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        <select value={getEdit(emp,"manager_id")} onChange={e=>setEdit(emp.id,"manager_id",e.target.value)}
                          style={{ ...inpSt, cursor:"pointer" }}>
                          <option value="" style={{background:T.opt_bg}}>— No manager —</option>
                          {employees
                            .filter(m => m.id !== emp.id)
                            .map(m=><option key={m.id} value={m.id} style={{ background:T.opt_bg }}>
                              {m.first_name} {m.last_name}{m.designation_title?` — ${m.designation_title}`:""}
                            </option>)}
                        </select>
                        {emp.manager_name && !rowEdits[emp.id]?.manager_id && (
                          <div style={{ fontSize:10.5, color:T.muted, marginTop:3 }}>Currently: {emp.manager_name}</div>
                        )}
                      </td>

                      {/* Account */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600, background:status.bg, color:status.color, whiteSpace:"nowrap" }}>{status.label}</span>
                      </td>

                      {/* Invite */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        {link ? (
                          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                            <div style={{ fontSize:10.5, fontFamily:"JetBrains Mono,monospace", color:T.muted, background:"rgba(0,0,0,0.2)", borderRadius:5, padding:"4px 7px", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={link}>{link}</div>
                            <button onClick={()=>copyLink(emp.id,link)} style={{ fontSize:11, padding:"2px 9px", borderRadius:6, border:`1px solid ${copied[emp.id]?`${T.green}4D`:`${T.cyan}33`}`, background:copied[emp.id]?`${T.green}0F`:`${T.cyan}0F`, color:copied[emp.id]?T.green:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"inline-flex", alignItems:"center", gap:3 }}>
                              {copied[emp.id]?<><Check size={9}/>Copied!</>:<><Copy size={9}/>Copy</>}
                            </button>
                          </div>
                        ) : (
                          <button onClick={()=>getInviteLink(emp)} disabled={sending[emp.id]}
                            style={{ fontSize:11.5, padding:"4px 10px", borderRadius:7, border:`1px solid ${T.cyan}26`, background:`${T.cyan}0F`, color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif", display:"inline-flex", alignItems:"center", gap:4, opacity:sending[emp.id]?0.6:1 }}>
                            <Mail size={11}/>{sending[emp.id]?"…":pu?"Resend":"Send Invite"}
                          </button>
                        )}
                      </td>

                      {/* Save */}
                      <td style={{ padding:"11px 14px", borderBottom:`1px solid ${T.cyan}0D` }}>
                        {pending ? (
                          <button onClick={()=>saveRow(emp)} disabled={saving[emp.id]}
                            style={{ fontSize:12, padding:"5px 12px", borderRadius:7, border:`1px solid ${T.cyan}40`, background:`linear-gradient(135deg,${T.cyan}26,${T.purple}26)`, color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontWeight:600, display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap" }}>
                            <Save size={11}/>{saving[emp.id]?"Saving…":"Save"}
                          </button>
                        ) : saved[emp.id] ? (
                          <span style={{ fontSize:12, color:T.green, display:"flex", alignItems:"center", gap:4 }}>
                            <Check size={12}/>Saved
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            T={T}
          />
        )}
      </div>
    </div>
  );
}